import {
  containerExplanation,
  type DeliveryRequest,
} from "../src/delivery.js";

/**
 * Getting a finished take out of the browser and onto the user's disk — the
 * page's half of the handover, because both ways out are window APIs.
 *
 * There are two, and which one a user gets is the browser's decision rather than
 * ours. The save dialog is Chromium's, it is the good one — the user picks the
 * folder and the name, once, at the end — and a plain download is the floor
 * every browser has. The dialog degrades to the download rather than to nothing.
 *
 * Everything here is behind ports for the usual reason: the decisions are worth
 * testing and none of them needs a browser to be tested. What genuinely does
 * need one — that `showSaveFilePicker` exists, that a real file lands — is a
 * human's to check, since a save dialog is by construction not automatable.
 */

/**
 * The finished take, as it goes out.
 *
 * A buffer rather than a typed view, because every platform call it is handed to
 * — the save dialog's writable and the `Blob` a download is made of — takes one,
 * and because a view is generic over its backing buffer in current TypeScript,
 * which turns "the bytes of a file" into a type argument nobody reading this
 * cares about.
 */
export type TakeBytes = ArrayBuffer;

export interface DeliveryPorts {
  /** Read the capture out of origin-private storage. */
  read(path: string): Promise<TakeBytes>;
  /**
   * Put it where the user says, through the browser's save dialog. Absent on a
   * browser that has no dialog, which is the whole reason it is optional.
   *
   * Rejects with `AbortError` when the user cancels, which is an answer and not
   * a failure — see {@link deliverTake}.
   */
  save?(suggestedName: string, bytes: TakeBytes): Promise<void>;
  /** The fallback: hand it to the browser's downloads. */
  download(suggestedName: string, bytes: TakeBytes): void;
}

export type DeliveryOutcome =
  | {
      ok: true;
      /** What the file was called. */
      output: string;
      /** What to say about the container, when it is not the one that was wanted. */
      explanation?: string;
    }
  | { ok: false; message: string };

/**
 * Deliver one take.
 *
 * The failure that matters is the cancel. The recording engine discards a
 * session the moment its bytes are reported safe somewhere else, so reporting
 * success for a dialog the user dismissed would delete the take they were still
 * deciding where to put. A cancel therefore comes back as *not delivered*, the
 * footage stays in storage, and web ticket 13 is what offers it again.
 *
 * A dialog refused for want of a user gesture is the opposite case and is
 * treated as one. Stopping a take is a gesture, but the flush between the stop
 * and the bytes being ready takes real time and the browser's activation window
 * is short — so falling through to the download, which needs no gesture, is the
 * difference between an awkward save and a lost take.
 */
export async function deliverTake(
  ports: DeliveryPorts,
  request: DeliveryRequest,
): Promise<DeliveryOutcome> {
  let bytes: TakeBytes;
  try {
    bytes = await ports.read(request.path);
  } catch (error) {
    return {
      ok: false,
      message: `Couldn't read the take back out of storage: ${describe(error)}`,
    };
  }

  const delivered = await putSomewhere(ports, request.fileName, bytes);
  if (!delivered.ok) return delivered;

  const explanation = containerExplanation(request.mimeType);
  return explanation === undefined
    ? { ok: true, output: request.fileName }
    : { ok: true, output: request.fileName, explanation };
}

async function putSomewhere(
  ports: DeliveryPorts,
  fileName: string,
  bytes: TakeBytes,
): Promise<{ ok: true } | { ok: false; message: string }> {
  const save = ports.save;
  if (save !== undefined) {
    try {
      await save(fileName, bytes);
      return { ok: true };
    } catch (error) {
      if (nameOf(error) === "AbortError") {
        return {
          ok: false,
          message: "The take wasn't saved — the save dialog was cancelled.",
        };
      }
      // Anything else is the dialog being unavailable in this moment rather than
      // the user declining it, and the take is worth more than the folder they
      // would have chosen.
    }
  }
  try {
    ports.download(fileName, bytes);
    return { ok: true };
  } catch (error) {
    return { ok: false, message: `Couldn't save the take: ${describe(error)}` };
  }
}

function nameOf(error: unknown): string {
  return typeof error === "object" && error !== null && "name" in error
    ? String((error as { name: unknown }).name)
    : "";
}

function describe(error: unknown): string {
  return error instanceof Error ? error.message : String(error);
}
